/*
 * The 10% email pop-up and pill (TN-17, TN-18; canonical D-41..D-46). Markup, the server gate
 * and the settings are snippets/email-popup.liquid; this file is only requested on pages where
 * that snippet renders.
 *
 * Start-up:
 *   1. `_kx` in the URL (arrived from a Klaviyo email) → mark subscribed, show nothing.
 *   2. Subscribed here before, or a stored signup from either surface (EwOffers.signup, the
 *      pop-up or the cart field) → show nothing.
 *   3. Count this eligible page view (`everwow:pv`, per session); a page restored by Back from
 *      the back/forward cache is a view too. Storage that throws counts as "unknown": the pill
 *      still shows, the pop-up never opens by itself.
 *   4. Read the cart once — the header's own /cart.js read (`ew:cart-snapshot`, up to 2.5 s),
 *      else our own — and stop on any discount code (D-45: ours, THANKYOU15 from redeem, a
 *      foreign one, live or dead) or any gift in the cart (PO-08).
 *   5. Show the pill once the visitor has scrolled one screen (`ew_offer_view {place:'pill'}`,
 *      once per page, when it is first seen).
 *   6. Auto-open, unless snoozed: on the second page once the page is idle (`page2`); on a
 *      timer on the landing page only if the setting allows it, or on later pages when the
 *      second-page rule is off (`timer`). Never over another dialog, the drawer, a nav panel,
 *      the cookie banner, the mobile search, a focused field or a hidden tab: a blocked open
 *      retries every second for 60 s, then gives up for the page.
 *   7. A gift or a code arriving later (add to cart, the cart's own changes, an offer applied)
 *      hides the pill and cancels the timers; so does a sign-up in the footer offer
 *      (`ew:signed-up`), in another tab, or on the page the back button returns from.
 *
 * Submit (Simple mode): validation here, then EwOffers.claim() (Klaviyo subscribe, stores the
 * signup), then EwOffers.apply() with source `popup`. On a cart without a gift, apply writes
 * nothing (`pending_gift`) and the cart applies the stored code on its next load (TC-13).
 * Klaviyo first: if it fails, no code (POP rule). The success step never shows the code; the
 * welcome email carries it.
 *
 * The only global is window.EwPopup = {open, state} for QA (plus `_test` for node --test).
 * EwOffers, EwKlaviyo and EwTrack are resolved when called, never captured at parse time.
 */
(function () {
  const KEY_SUBSCRIBED = 'everwow:offer-subscribed';
  const KEY_SNOOZE = 'everwow:popup-snooze';
  const KEY_PV = 'everwow:pv';
  const KEY_SIGNUP = 'ew:signup';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const SNAPSHOT_WAIT_MS = 2500;
  const RETRY_EVERY_MS = 1000;
  const RETRY_FOR_MS = 60000;
  const IDLE_TIMEOUT_MS = 1500;
  const CLOSE_MS = 190;
  const GIFT_TYPES = ['experience-gift', 'gift-card'];
  // The shape check TC-13 and BE-12 use, on the lowercased address: a client looser than the
  // server sends addresses that come back `invalid` and count as failures.
  const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;

  /* ------------------------------------------------------------------ pure logic */

  function cartHasGift(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (item) {
      return (
        GIFT_TYPES.indexOf(String(item.product_type || '').toLowerCase()) !== -1
      );
    });
  }

  function cartHasCode(cart) {
    return !!(
      cart &&
      Array.isArray(cart.discount_codes) &&
      cart.discount_codes.length > 0
    );
  }

  /*
   * Everything the pop-up decides on start-up, pure.
   *   input: {pv, snoozeUntil, now, subscribed, signup, kx, arrivalCode, cart, mode,
   *           settings: {openSecond, openLanding}}
   *   → {pill: bool, auto: 'page2' | 'timer' | null, reason}
   * `pv` null means storage failed. `cart` null means it could not be read: the pill still
   * shows (the server already checked the cart), but nothing opens by itself.
   */
  function decide(input) {
    const none = function (reason) {
      return { pill: false, auto: null, reason: reason };
    };
    if (input.mode !== 'simple' && input.mode !== 'claim') return none('mode');
    if (input.kx) return none('kx');
    if (input.subscribed) return none('subscribed');
    if (input.signup) return none('signup');
    if (input.arrivalCode) return none('arrival');
    if (cartHasCode(input.cart)) return none('code');
    if (cartHasGift(input.cart)) return none('gift');

    const settings = input.settings || {};
    const show = function (auto, reason) {
      return { pill: true, auto: auto, reason: reason };
    };
    if (!input.cart) return show(null, 'no-cart');
    if (
      typeof input.snoozeUntil === 'number' &&
      input.snoozeUntil > input.now
    ) {
      return show(null, 'snoozed');
    }
    if (typeof input.pv !== 'number') return show(null, 'no-storage');
    if (input.pv >= 2 && settings.openSecond) return show('page2', 'page2');
    if (settings.openLanding || (input.pv >= 2 && !settings.openSecond)) {
      return show('timer', 'timer');
    }
    return show(null, 'landing');
  }

  /*
   * What the form shows after EwOffers.claim() and, for a sign-up, EwOffers.apply().
   *   → {view: 'success' | 'error', error, line: bool, applied: bool}
   */
  function resultView(status, applyResult) {
    const error = function (key) {
      return { view: 'error', error: key, line: false, applied: false };
    };
    if (status === 'subscribed') {
      switch (applyResult) {
        case 'applied':
          return { view: 'success', error: null, line: true, applied: true };
        case 'pending_gift':
        case 'network':
          // The code waits in the stored signup; the cart applies it on its next load.
          return { view: 'success', error: null, line: true, applied: false };
        case 'not_applicable':
          // WELCOME10 is off or expired. The subscription stands; the signup lets the cart retry.
          return error('paused');
        default:
          // kept_better, blocked_foreign, pending_checkout, or no code configured: saved, but
          // "applied" would not be true.
          return { view: 'success', error: null, line: false, applied: false };
      }
    }
    if (status === 'paused') return error('paused');
    return error('network');
  }

  function validEmail(value) {
    return EMAIL.test(value);
  }

  /* -------------------------------------------------------------------- storage */

  function store(kind) {
    try {
      return window[kind] || null;
    } catch (error) {
      return null;
    }
  }

  function readKey(kind, key) {
    try {
      const target = store(kind);
      return target ? target.getItem(key) : null;
    } catch (error) {
      return null;
    }
  }

  function writeKey(kind, key, value) {
    try {
      const target = store(kind);
      if (target) target.setItem(key, value);
    } catch (error) {
      // Blocked storage: the pop-up still works, it just forgets.
    }
  }

  // The session's eligible page views, counted up; null when storage throws.
  function countView() {
    try {
      const target = window.sessionStorage;
      const next = (parseInt(target.getItem(KEY_PV), 10) || 0) + 1;
      target.setItem(KEY_PV, String(next));
      return next;
    } catch (error) {
      return null;
    }
  }

  function isSubscribed() {
    return readKey('localStorage', KEY_SUBSCRIBED) === '1';
  }

  function storedSignup() {
    try {
      const offers = window.EwOffers;
      if (offers && offers.signup) return offers.signup.get();
    } catch (error) {
      return null;
    }
    return null;
  }

  // TN-26 (spike, 2026-09-27): a `/discount/<code>` arrival creates the cart and /cart.js
  // lists the code at once, with no line yet (`applicable: false`), so step 4 already catches
  // it. Shopify also sets a readable `discount_code` cookie (no HttpOnly); it is checked too, in
  // case the header's cart read has not happened. It lasts the browser session.
  function arrivalCode() {
    try {
      const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function track(event, params) {
    if (window.EwTrack) window.EwTrack.push(event, params || {});
  }

  /* --------------------------------------------------------------------- runtime */

  const state = {
    decision: null,
    pv: null,
    counted: false,
    cart: null,
    pillViewed: false,
    timers: [],
    retry: null,
    busy: false,
    success: false,
    lastTrigger: null,
    scroll: null,
  };

  let dialog = null;
  let pill = null;
  let form = null;
  let done = null;
  let email = null;
  let honeypot = null;
  let errorLine = null;
  let submit = null;
  let settings = null;

  function flag(name) {
    return dialog.getAttribute('data-' + name) === 'true';
  }

  function i18n(key) {
    return dialog.getAttribute('data-i18n-' + key) || '';
  }

  function reducedMotion() {
    return (
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function clearTimers() {
    state.timers.forEach(function (id) {
      clearTimeout(id);
    });
    state.timers = [];
    if (state.retry) clearInterval(state.retry);
    state.retry = null;
  }

  // The pill waits for one screen of scrolling. Shown on load it sat on the home hero: on the
  // headline of a 320px phone, on a chip of a 390px one. It leaves again near the top for the
  // same reason. A page too short to scroll a full screen shows it once its end is reached.
  // While it waits it stays in the layout, invisible (`data-waiting`, styled in app.scss),
  // the way it always was from load: taken out with `hidden` and brought back mid-scroll, an
  // iPhone placed it a toolbar's height above the bottom of the screen.
  let pillWanted = false;
  let pastFirstScreen = false;

  function renderPill() {
    if (!pill) return;
    const visible = pillWanted && pastFirstScreen;
    pill.hidden = !pillWanted;
    pill.toggleAttribute('data-waiting', !visible);
    pill.inert = !visible;
    if (visible && !state.pillViewed) {
      state.pillViewed = true;
      track('ew_offer_view', { place: 'pill' });
    }
  }

  function showPill(visible) {
    pillWanted = visible;
    renderPill();
  }

  function watchFirstScreen() {
    const check = function () {
      const screen = window.innerHeight;
      const room = document.documentElement.scrollHeight - screen;
      const past = window.scrollY >= Math.max(0, Math.min(screen, room));
      if (past === pastFirstScreen) return;
      pastFirstScreen = past;
      renderPill();
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    check();
  }

  function stopAll() {
    clearTimers();
    showPill(false);
  }

  function visible(el) {
    return !!(el && !el.hidden && !el.classList.contains('hidden'));
  }

  // Anything that must not be covered, or that the pop-up must not steal focus from.
  function isBlocked() {
    const openDialogs = document.querySelectorAll('dialog[open]');
    for (let i = 0; i < openDialogs.length; i++) {
      if (openDialogs[i] !== dialog) return true;
    }
    const modals = document.querySelectorAll('[aria-modal="true"]');
    for (let i = 0; i < modals.length; i++) {
      if (modals[i] !== dialog && visible(modals[i])) return true;
    }
    if (visible(document.querySelector('[data-cookie-banner]'))) return true;
    if (visible(document.querySelector('[data-mobile-search-modal]')))
      return true;
    if (document.documentElement.hasAttribute('data-nav-panel-open'))
      return true;
    if (document.visibilityState && document.visibilityState !== 'visible')
      return true;
    const active = document.activeElement;
    if (
      active &&
      active !== document.body &&
      !dialog.contains(active) &&
      (/^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) ||
        active.isContentEditable)
    ) {
      return true;
    }
    return false;
  }

  // An auto-open waits while something is in the way: every second for up to a minute.
  function tryAutoOpen(trigger) {
    if (state.success || dialog.open) return;
    const attempt = function () {
      if (!state.decision || !state.decision.pill || state.success) {
        clearInterval(state.retry);
        state.retry = null;
        return true;
      }
      if (isBlocked()) return false;
      clearInterval(state.retry);
      state.retry = null;
      open(trigger);
      return true;
    };
    if (attempt()) return;
    const started = Date.now();
    if (state.retry) clearInterval(state.retry);
    state.retry = setInterval(function () {
      if (attempt()) return;
      if (Date.now() - started >= RETRY_FOR_MS) {
        clearInterval(state.retry);
        state.retry = null;
      }
    }, RETRY_EVERY_MS);
  }

  function scheduleAutoOpen(auto) {
    if (auto === 'page2') {
      // Never before the page settles: after `load`, then once the main thread is idle.
      const whenIdle = function () {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(
            function () {
              tryAutoOpen('page2');
            },
            { timeout: IDLE_TIMEOUT_MS },
          );
        } else {
          state.timers.push(
            setTimeout(function () {
              tryAutoOpen('page2');
            }, IDLE_TIMEOUT_MS),
          );
        }
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    } else if (auto === 'timer') {
      state.timers.push(
        setTimeout(function () {
          tryAutoOpen('timer');
        }, settings.delay * 1000),
      );
    }
  }

  function evaluate(cart) {
    state.cart = cart;
    const snoozeRaw = readKey('localStorage', KEY_SNOOZE);
    state.decision = decide({
      pv: state.pv,
      snoozeUntil: snoozeRaw ? Number(snoozeRaw) : null,
      now: Date.now(),
      subscribed: isSubscribed(),
      signup: storedSignup(),
      kx: false,
      arrivalCode: arrivalCode(),
      cart: cart,
      mode: settings.mode,
      settings: settings,
    });
    return state.decision;
  }

  // Later cart changes can only take the offer away: a gift or a code arriving hides the pill
  // and cancels any pending open. They never schedule a new auto-open.
  function reevaluate(cart) {
    if (state.success) return;
    const apply = function (current) {
      const decision = evaluate(current || state.cart);
      if (!decision.pill) stopAll();
    };
    if (cart) apply(cart);
    else readCart().then(apply, function () {});
  }

  function readCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    });
  }

  // The header's /cart.js read (theme.js HeaderCartCount) if it lands in time, else our own.
  // `fresh` waits for the next read: a back/forward-cache restore brings the snapshot back from
  // before the shopper left, and the header reads the cart again on that restore.
  function firstCart(fresh) {
    if (!fresh && window.EwCartSnapshot)
      return Promise.resolve(window.EwCartSnapshot);
    return new Promise(function (resolve) {
      let settled = false;
      const finish = function (cart) {
        if (settled) return;
        settled = true;
        document.removeEventListener('ew:cart-snapshot', onSnapshot);
        resolve(cart);
      };
      const onSnapshot = function (event) {
        finish(event.detail || null);
      };
      document.addEventListener('ew:cart-snapshot', onSnapshot);
      setTimeout(function () {
        if (settled) return;
        readCart().then(finish, function () {
          finish(null);
        });
      }, SNAPSHOT_WAIT_MS);
    });
  }

  function start(fresh) {
    clearTimers();
    if (/[?&]_kx=/.test(window.location.search)) {
      writeKey('localStorage', KEY_SUBSCRIBED, '1');
      state.decision = { pill: false, auto: null, reason: 'kx' };
      stopAll();
      return;
    }
    if (isSubscribed() || storedSignup()) {
      state.decision = { pill: false, auto: null, reason: 'subscribed' };
      stopAll();
      return;
    }
    if (state.pv === null && !state.counted) {
      state.counted = true;
      state.pv = countView();
    }
    firstCart(fresh).then(function (cart) {
      const decision = evaluate(cart);
      showPill(decision.pill);
      if (decision.auto) scheduleAutoOpen(decision.auto);
    });
  }

  /* ------------------------------------------------------------ open and close */

  function lockScroll() {
    const root = document.documentElement;
    const gutter = window.innerWidth - root.clientWidth;
    state.scroll = {
      overflow: root.style.overflow,
      paddingRight: root.style.paddingRight,
    };
    root.style.overflow = 'hidden';
    if (gutter > 0) root.style.paddingRight = gutter + 'px';
  }

  function unlockScroll() {
    if (!state.scroll) return;
    const root = document.documentElement;
    root.style.overflow = state.scroll.overflow;
    root.style.paddingRight = state.scroll.paddingRight;
    state.scroll = null;
  }

  function open(trigger) {
    if (!dialog || dialog.open) return;
    clearTimers();
    dialog.removeAttribute('data-closing');
    try {
      dialog.showModal();
    } catch (error) {
      return;
    }
    lockScroll();
    state.lastTrigger = trigger || 'pill';
    // The heading takes focus, not the email field: the offer opens at rest, with no
    // active caret or phone keyboard, and screen readers still start on the title.
    const target = (state.success ? done : form).querySelector('h2');
    if (target) target.focus({ preventScroll: true });
    track('ew_offer_view', { place: 'popup', trigger: state.lastTrigger });
  }

  function close(snooze) {
    if (!dialog || !dialog.open || dialog.hasAttribute('data-closing')) return;
    if (snooze && !state.success) {
      writeKey(
        'localStorage',
        KEY_SNOOZE,
        String(Date.now() + settings.snoozeDays * DAY_MS),
      );
    }
    const finish = function () {
      if (dialog.open) dialog.close();
      onClosed();
    };
    if (reducedMotion()) {
      finish();
      return;
    }
    dialog.setAttribute('data-closing', '');
    setTimeout(finish, CLOSE_MS);
  }

  // Runs on every close, animated or not, and at most once per open: from the close timer and
  // from the `close` event. Chrome's close watcher can close a modal without a preventable
  // `cancel` (a second Esc), and a hidden tab never dispatches `close` at all, so neither path
  // alone is enough.
  function onClosed() {
    if (!state.scroll && !dialog.hasAttribute('data-closing')) return;
    dialog.removeAttribute('data-closing');
    unlockScroll();
    if (!state.success && state.decision && state.decision.pill) showPill(true);
    if (pill && !pill.hidden && !pill.inert)
      pill.focus({ preventScroll: true });
    else if (document.body) document.body.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------- submit */

  function showError(key) {
    errorLine.textContent = i18n('error-' + key);
    errorLine.hidden = false;
    email.setAttribute('aria-invalid', 'true');
    email.focus({ preventScroll: true });
  }

  function clearError() {
    errorLine.hidden = true;
    errorLine.textContent = '';
    email.removeAttribute('aria-invalid');
  }

  function setBusy(busy) {
    state.busy = busy;
    submit.disabled = busy;
    if (busy) submit.setAttribute('aria-busy', 'true');
    else submit.removeAttribute('aria-busy');
  }

  function showSuccess(view) {
    state.success = true;
    writeKey('localStorage', KEY_SUBSCRIBED, '1');
    clearTimers();
    showPill(false);
    const line = done.querySelector('[data-ew-popup-line]');
    if (line) line.hidden = !view.line;
    form.hidden = true;
    done.hidden = false;
    dialog.setAttribute('aria-labelledby', 'ew-popup-done-title');
    const title = done.querySelector('h2');
    if (title) title.focus({ preventScroll: true });
    // The footer offer flips to its own success step (assets/footer-offer.js).
    document.dispatchEvent(
      new CustomEvent('ew:signed-up', { detail: { place: 'popup' } }),
    );
    if (!reducedMotion() && navigator.vibrate) {
      try {
        navigator.vibrate(12);
      } catch (error) {
        // No haptics here.
      }
    }
  }

  function onSubmit(event) {
    event.preventDefault();
    if (state.busy) return;
    const address = String(email.value || '')
      .trim()
      .toLowerCase();
    if (!address) {
      showError('empty');
      return;
    }
    if (!validEmail(address)) {
      showError('invalid');
      return;
    }
    clearError();

    const hp = honeypot && honeypot.value ? honeypot.value : '';
    if (hp && settings.mode === 'simple') {
      // A bot: a success that sends nothing.
      showSuccess({ line: true });
      return;
    }

    setBusy(true);
    track('ew_email_submit', { place: 'popup' });

    const offers = window.EwOffers;
    const claimed =
      offers && typeof offers.claim === 'function'
        ? offers.claim(address, { place: 'popup', hp: hp })
        : Promise.resolve({ status: 'network' });

    claimed
      .catch(function () {
        return { status: 'network' };
      })
      .then(function (claim) {
        const status = (claim && claim.status) || 'network';
        if (status !== 'subscribed' || !claim.code) {
          return { claim: claim || {}, status: status, applied: null };
        }
        return window.EwOffers.apply(claim.code, { source: 'popup' }).then(
          function (outcome) {
            return {
              claim: claim,
              status: status,
              applied: outcome && outcome.result,
            };
          },
          function () {
            return { claim: claim, status: status, applied: 'network' };
          },
        );
      })
      .then(function (outcome) {
        setBusy(false);
        const view = resultView(outcome.status, outcome.applied);
        track('ew_email_result', { place: 'popup', status: outcome.status });
        if (view.view === 'error') {
          showError(view.error);
          return;
        }
        if (view.applied) {
          track('ew_offer_apply', {
            offer: outcome.claim.offer || 'wc10',
            place: 'popup',
          });
        }
        try {
          if (window.EwKlaviyo) window.EwKlaviyo.identify(address);
        } catch (error) {
          // identify is fire-and-forget.
        }
        showSuccess(view);
      });
  }

  /* -------------------------------------------------------------------- wiring */

  // Keeps the pill clear of the PDP's sticky buy bar: its height, for the lift rule in app.scss.
  function watchStickyBar() {
    const bar = document.querySelector('[data-sticky-atc]');
    if (!bar || typeof ResizeObserver !== 'function') return;
    const write = function () {
      document.documentElement.style.setProperty(
        '--ew-sticky-atc-h',
        bar.offsetHeight + 'px',
      );
    };
    new ResizeObserver(write).observe(bar);
    write();
  }

  function bind() {
    pill.addEventListener('click', function () {
      track('ew_pill_click', {});
      open('pill');
    });
    dialog
      .querySelector('[data-ew-popup-close]')
      .addEventListener('click', function () {
        close(true);
      });
    dialog
      .querySelector('[data-ew-popup-not-now]')
      .addEventListener('click', function () {
        close(true);
      });
    dialog
      .querySelector('[data-ew-popup-shop]')
      .addEventListener('click', function () {
        close(false);
        // Oct 31 to Dec 30 the button reads "Shop Christmas gifts" (snippets/email-popup.liquid) and opens that
        // collection; the URL carries the market's prefix, as location.pathname does.
        const url = this.getAttribute('data-ew-popup-shop-url');
        if (
          url &&
          document.documentElement.classList.contains('ew-xmas') &&
          window.location.pathname !== url
        ) {
          window.location.assign(url);
        }
      });
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      close(true);
    });
    dialog.addEventListener('close', onClosed);
    // The backdrop, from `md` up. Below it the dialog fills the screen: a tap on its padding
    // targets the dialog too, and must not dismiss it.
    dialog.addEventListener('click', function (event) {
      if (event.target !== dialog) return;
      if (!window.matchMedia('(min-width: 750px)').matches) return;
      close(true);
    });
    form.addEventListener('submit', onSubmit);
    email.addEventListener('input', function () {
      if (
        !errorLine.hidden &&
        validEmail(
          String(email.value || '')
            .trim()
            .toLowerCase(),
        )
      ) {
        clearError();
      }
    });

    document.addEventListener('product:added-to-cart', function () {
      reevaluate(null);
    });
    document.addEventListener('cart:changed', function () {
      reevaluate(null);
    });
    document.addEventListener('ew:cart-snapshot', function (event) {
      if (state.decision) reevaluate(event.detail || null);
    });
    document.addEventListener('ew:offers-changed', function (event) {
      reevaluate((event.detail && event.detail.cart) || null);
    });
    // Back to this page from another, restored from the back/forward cache (Safari does it on
    // nearly every Back): a new view of the page, counted and decided like a fresh load. Without
    // the count, a shopper who landed here, looked at the cart and came back stayed on the
    // landing decision — the pill and never the pop-up, where a reload opened it. It still
    // covers a sign-up on the page they come back from: that page's pill must not return here.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      if (dialog.open) dialog.close();
      state.pv = null;
      state.counted = false;
      state.pillViewed = false;
      start(true);
    });
    // A sign-up in the footer offer on this page.
    document.addEventListener('ew:signed-up', function (event) {
      if (event.detail && event.detail.place === 'popup') return;
      if (!state.success) stopAll();
    });
    // A sign-up in another tab.
    window.addEventListener('storage', function (event) {
      if (event.key === KEY_SUBSCRIBED || event.key === KEY_SIGNUP) {
        if (!state.success && (isSubscribed() || storedSignup())) stopAll();
      }
    });
  }

  function init() {
    dialog = document.querySelector('[data-ew-popup]');
    pill = document.querySelector('[data-ew-pill]');
    if (!dialog || !pill || dialog.__ewPopup) return;
    dialog.__ewPopup = true;
    form = dialog.querySelector('[data-ew-popup-form]');
    done = dialog.querySelector('[data-ew-popup-done]');
    email = dialog.querySelector('[data-ew-popup-email]');
    honeypot = dialog.querySelector('[data-ew-popup-hp]');
    errorLine = dialog.querySelector('[data-ew-popup-error]');
    submit = dialog.querySelector('[data-ew-popup-submit]');
    settings = {
      mode: dialog.getAttribute('data-mode'),
      delay: Math.max(0, parseInt(dialog.getAttribute('data-delay'), 10) || 12),
      snoozeDays: Math.max(
        1,
        parseFloat(dialog.getAttribute('data-snooze-days')) || 7,
      ),
      openSecond: flag('open-second'),
      openLanding: flag('open-landing'),
    };
    bind();
    watchStickyBar();
    watchFirstScreen();
    start();
  }

  window.EwPopup = {
    open: function () {
      open('pill');
    },
    state: function () {
      return {
        decision: state.decision,
        pv: state.pv,
        success: state.success,
        open: !!(dialog && dialog.open),
        blocked: dialog ? isBlocked() : null,
      };
    },
    _test: {
      decide: decide,
      resultView: resultView,
      validEmail: validEmail,
      cartHasGift: cartHasGift,
      cartHasCode: cartHasCode,
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
